import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { TrendingUp, Loader2, Info } from "lucide-react";

const COLORS = ["#2563eb", "#16a34a", "#d97706", "#9333ea", "#0891b2", "#dc2626", "#4f46e5", "#65a30d"];

export default function CompetitorPriceHistory({ propId, lang = "it" }) {
  const L = (it, en) => (lang === "en" ? en : it);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!propId) return;
    setLoading(true);
    api.get(`/properties/${propId}/competitors-price-trend?lang=${lang}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [propId, lang]);

  const comps = data?.competitors || [];
  const market = data?.market || [];

  const merged = useMemo(() => {
    const byDay = {};
    (data?.days || []).forEach((d) => { byDay[d] = { day: d }; });
    market.forEach((m) => { if (byDay[m.day]) byDay[m.day].__market = m.avg; });
    comps.forEach((c) => c.series.forEach((s) => { if (byDay[s.day]) byDay[s.day][c.name] = s.price; }));
    return Object.values(byDay);
  }, [data, comps, market]);

  const fmtDay = (d) => (d ? String(d).slice(5) : "");

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="bg-card border border-border rounded-lg p-6 md:col-span-4" data-testid="competitor-price-history">
      <div className="flex items-center gap-2 mb-1">
        <TrendingUp className="w-5 h-5 text-primary" />
        <h2 className="text-lg font-semibold">{L("Storico prezzi competitor", "Competitor price history")}</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        {L("Andamento delle tariffe dei tuoi competitor selezionati nel tempo. La linea tratteggiata è la media di mercato.",
          "How your selected competitors' rates evolved over time. The dashed line is the market average.")}
        {data?.source === "fallback" && ` · ${L("(competitor più simili — nessuna selezione manuale ancora)", "(most similar competitors — no manual selection yet)")}`}
      </p>

      {loading ? (
        <div className="py-16 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />{L("Carico lo storico…", "Loading history…")}</div>
      ) : merged.length < 2 ? (
        <div className="py-10 text-center text-sm text-muted-foreground flex flex-col items-center gap-2" data-testid="cph-empty">
          <Info className="w-5 h-5" />
          {L("Storico ancora limitato: lo scanner raccoglie i prezzi ogni giorno. Il grafico si arricchisce col passare dei giorni.",
            "History still limited: the scanner collects prices daily. The chart fills in over the coming days.")}
        </div>
      ) : (
        <div style={{ width: "100%", height: 320 }}>
          <ResponsiveContainer>
            <LineChart data={merged} margin={{ top: 8, right: 16, left: -12, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.4} />
              <XAxis dataKey="day" tickFormatter={fmtDay} tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `€${v}`} width={52} />
              <Tooltip formatter={(v, n) => [`€${v}`, n === "__market" ? L("Media mercato", "Market avg") : n]}
                labelFormatter={(d) => fmtDay(d)} contentStyle={{ fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => (v === "__market" ? L("Media mercato", "Market avg") : v)} />
              {comps.map((c, i) => (
                <Line key={c.competitor_id} type="monotone" dataKey={c.name} stroke={COLORS[i % COLORS.length]}
                  strokeWidth={2} dot={{ r: 3 }} connectNulls />
              ))}
              <Line type="monotone" dataKey="__market" stroke="var(--primary)" strokeWidth={3}
                strokeDasharray="6 4" dot={false} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </motion.div>
  );
}
